import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { createArticle, updateArticle, getAdminArticles, uploadFile } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Save, Eye, Upload, X, Share2, Copy, Facebook, Bell, Clock, FileText, PlayCircle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import RichTextEditor from "@/components/RichTextEditor";
import AdminLayout from '@/components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  "Cincy Feed", "Check On Yo Peoples News Feed", "Cincy Worldstar", "Check Your Claims"
];
const POST_TYPES = [
  {
    value: 'article',
    label: 'Article',
    Icon: FileText,
    description: 'Standard written story',
  },
  {
    value: 'video',
    label: 'Video',
    Icon: PlayCircle,
    description: 'Video-style post with play treatment in admin',
  },
];

const AdminArticleForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [publishedSlug, setPublishedSlug] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'Check On Yo Peoples News Feed',
    post_type: 'article',
    featured_image: '',
    author: 'CincyPulse',
    is_featured: false,
    status: 'published', // Default to published for instant feed
    scheduled_at: '',
    ad_code_top: '',
    ad_code_middle: '',
    ad_code_bottom: '',
  });

  const categoryOptions = Array.from(new Set([...CATEGORIES, formData.category].filter(Boolean)));

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }

    if (isEdit) {
      fetchArticle();
    } else {
      // Check for AI-generated draft
      const draft = sessionStorage.getItem('ai_article_draft');
      if (draft) {
        try {
          const parsed = JSON.parse(draft);
          setFormData(prev => ({
            ...prev,
            title: parsed.title || prev.title,
            content: parsed.content || prev.content,
            featured_image: parsed.featured_image || prev.featured_image,
            post_type: parsed.post_type || prev.post_type,
          }));
          sessionStorage.removeItem('ai_article_draft');
        } catch (e) {
          // ignore
        }
      }
    }
  }, [isAuthenticated, navigate, isEdit, id]);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const response = await getAdminArticles();
      const article = response.data.find((a) => a.id === id);
      if (article) {
        setFormData({
          title: article.title || '',
          content: article.content || '',
          category: article.category || 'Check On Yo Peoples News Feed',
          post_type: article.post_type || 'article',
          featured_image: article.featured_image || '',
          author: article.author || 'CincyPulse',
          is_featured: article.is_featured || false,
          status: article.status || 'published',
          scheduled_at: article.scheduled_at || '',
          ad_code_top: article.ad_code_top || '',
          ad_code_middle: article.ad_code_middle || '',
          ad_code_bottom: article.ad_code_bottom || '',
        });
      } else {
        toast.error('Story not found');
        navigate('/admin/articles');
      }
    } catch (error) {
      toast.error('Failed to fetch story');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image or video file');
      return;
    }

    // Check size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Max 50MB.');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadFile(file);
      const fileUrl = `${BACKEND_URL}/api/files/${response.data.id}`;
      setFormData({ ...formData, featured_image: fileUrl });
      toast.success('File uploaded!');
    } catch (error) {
      toast.error('Upload failed. Try again.');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.content) {
      toast.error('Please fill in content');
      return;
    }

    if (formData.status === 'scheduled' && !formData.scheduled_at) {
      toast.error('Please pick a scheduled time');
      return;
    }
    if (formData.status === 'scheduled' && new Date(formData.scheduled_at) <= new Date()) {
      toast.error('Scheduled time must be in the future');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...formData };
      if (payload.status !== 'scheduled') payload.scheduled_at = null;

      if (isEdit) {
        await updateArticle(id, payload);
        toast.success(formData.status === 'scheduled' ? 'Story scheduled!' : 'Story updated!');
        // Stay on the edit page
      } else {
        const response = await createArticle(payload);
        toast.success(formData.status === 'scheduled' ? 'Story scheduled!' : 'Story published!');
        // Navigate to edit the new article
        if (response.data?.id) {
          navigate(`/admin/articles/edit/${response.data.id}`);
        } else {
          navigate('/admin/articles');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save story');
    } finally {
      setSaving(false);
    }
  };

  const clearImage = () => {
    setFormData({ ...formData, featured_image: '' });
  };

  const setScheduleInHours = (hours) => {
    const target = new Date(Date.now() + hours * 60 * 60 * 1000);
    setFormData({ ...formData, scheduled_at: target.toISOString() });
  };

  const toLocalInputValue = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const scheduleCountdown = () => {
    if (!formData.scheduled_at) return null;
    const diffMs = new Date(formData.scheduled_at).getTime() - Date.now();
    if (diffMs <= 0) return 'Publishing any moment now...';
    const hrs = Math.floor(diffMs / (60 * 60 * 1000));
    const mins = Math.floor((diffMs % (60 * 60 * 1000)) / (60 * 1000));
    return `Posts in ${hrs > 0 ? `${hrs}h ` : ''}${mins}m — ${new Date(formData.scheduled_at).toLocaleString()}`;
  };

  if (!isAuthenticated) return null;

  return (
    <AdminLayout>
      <div data-testid="admin-article-form">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/admin/articles"
            className="p-2 text-gray-400 hover:text-white hover:bg-[#222] rounded transition-colors"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h2 className="text-2xl font-bold text-white">
              {isEdit ? 'Edit Story' : 'Add New Story'}
            </h2>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Publish Bar - full-width, above the title so status/scheduling is decided first */}
            <div className="bg-[#111] border border-[#222] p-6 mb-6" data-testid="publish-bar">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-yellow-500">Status</span>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger className="w-36 bg-black border-[#222] text-white" data-testid="article-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-[#222]">
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-400">Featured</span>
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                    data-testid="article-featured-switch"
                  />
                </div>

                <div className="flex items-center gap-2" data-testid="post-type-toggle-group">
                  <span className="text-xs font-bold uppercase tracking-wider text-yellow-500">Type</span>
                  <div className="flex items-center gap-2">
                    {POST_TYPES.map(({ value, label, Icon }) => {
                      const active = formData.post_type === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setFormData({ ...formData, post_type: value })}
                          className={`inline-flex items-center gap-2 px-3 py-2 border text-xs font-bold uppercase tracking-wider transition-colors ${
                            active
                              ? 'border-yellow-500 bg-yellow-500/15 text-yellow-400 shadow-[0_0_20px_rgba(239,68,68,0.12)]'
                              : 'border-[#333] bg-black text-gray-400 hover:border-yellow-500/60 hover:text-white'
                          }`}
                          data-testid={`post-type-toggle-${value}`}
                        >
                          <Icon size={14} />
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-auto">
                  {isEdit && (
                    <Link
                      to={`/article/${id}`}
                      target="_blank"
                      className="flex items-center gap-2 px-4 py-3 border border-[#222] text-gray-400 hover:text-white hover:border-white transition-colors"
                    >
                      <Eye size={16} />
                      Preview
                    </Link>
                  )}
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-yellow-500 text-white font-bold hover:bg-yellow-600 transition-colors disabled:opacity-50"
                    data-testid="save-article-btn"
                  >
                    {saving ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Save size={18} />
                    )}
                    {saving ? 'Saving...' : (isEdit ? 'Update' : (formData.status === 'scheduled' ? 'Schedule' : 'Publish'))}
                  </button>
                </div>
              </div>

              {formData.status === 'scheduled' && (
                <div className="pt-4 mt-4 border-t border-[#222]" data-testid="schedule-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock size={14} className="text-yellow-500" />
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Post In</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {[1, 2, 3, 6, 12, 24].map((h) => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => setScheduleInHours(h)}
                        className="px-4 py-2 text-xs font-medium bg-black border border-[#333] text-gray-300 hover:border-yellow-500 hover:text-yellow-400 transition-colors"
                        data-testid={`schedule-preset-${h}h`}
                      >
                        {h}h
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <label className="text-[11px] text-gray-500 block mb-1">Or pick exact date & time</label>
                      <input
                        type="datetime-local"
                        value={toLocalInputValue(formData.scheduled_at)}
                        onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                        className="px-3 py-2 bg-black border border-[#222] text-white text-sm focus:outline-none focus:border-yellow-500"
                        data-testid="schedule-datetime-input"
                      />
                    </div>
                    {formData.scheduled_at && (
                      <p className="text-xs text-yellow-400" data-testid="schedule-countdown">
                        {scheduleCountdown()}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="pt-4 mt-4 border-t border-[#222]" data-testid="post-type-helper-panel">
                <div className={`flex items-start gap-3 px-4 py-3 border ${formData.post_type === 'video' ? 'border-yellow-500/40 bg-yellow-500/10' : 'border-[#222] bg-black/40'}`}>
                  {formData.post_type === 'video' ? <PlayCircle size={18} className="text-yellow-400 mt-0.5" /> : <FileText size={18} className="text-gray-400 mt-0.5" />}
                  <div>
                    <p className={`text-sm font-bold ${formData.post_type === 'video' ? 'text-yellow-300' : 'text-white'}`} data-testid="post-type-helper-title">
                      {formData.post_type === 'video' ? 'Video post selected' : 'Article selected'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1" data-testid="post-type-helper-description">
                      {POST_TYPES.find((item) => item.value === formData.post_type)?.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Form */}
              <div className="lg:col-span-2 space-y-6">
                {/* Title */}
                <div className="bg-[#111] border border-[#222] p-6">
                  <label className="text-xs font-bold uppercase tracking-wider text-yellow-500 block mb-3">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-4 bg-black border border-[#222] text-white text-xl focus:outline-none focus:border-yellow-500"
                    placeholder="Story title..."
                    required
                    data-testid="article-title-input"
                  />
                </div>

                {/* Content - Rich Text Editor (supports HTML) */}
                <div className="bg-[#111] border border-[#222] p-6">
                  <label className="text-xs font-bold uppercase tracking-wider text-yellow-500 block mb-3">
                    Content * <span className="text-gray-500 font-normal">(supports HTML)</span>
                  </label>
                  <RichTextEditor
                    value={formData.content}
                    onChange={(content) => setFormData({ ...formData, content })}
                    placeholder="Write your story... Use toolbar for formatting, images, and videos"
                  />
                </div>

                {/* Featured Image/Video Upload */}
                <div className="bg-[#111] border border-[#222] p-6">
                  <label className="text-xs font-bold uppercase tracking-wider text-yellow-500 block mb-3">
                    Featured Image / Video
                  </label>
                  
                  {formData.featured_image ? (
                    <div className="relative">
                      <img
                        src={formData.featured_image}
                        alt="Featured"
                        className="w-full h-48 object-cover border border-[#222]"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23222" width="100" height="100"/><text x="50%" y="50%" fill="%23666" text-anchor="middle" dy=".3em">Media</text></svg>';
                        }}
                      />
                      <button
                        type="button"
                        onClick={clearImage}
                        className="absolute top-2 right-2 p-2 bg-black/80 text-white hover:bg-yellow-500 rounded transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-[#333] hover:border-yellow-500 p-8 text-center cursor-pointer transition-colors"
                    >
                      {uploading ? (
                        <Loader2 className="w-8 h-8 animate-spin text-yellow-500 mx-auto" />
                      ) : (
                        <>
                          <Upload size={32} className="mx-auto text-gray-500 mb-2" />
                          <p className="text-gray-400">Click to upload image or video</p>
                          <p className="text-gray-600 text-xs mt-1">JPG, PNG, GIF, WebP, MP4, WebM (max 50MB)</p>
                        </>
                      )}
                    </div>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-upload-input"
                  />

                  {/* Or paste URL */}
                  <div className="mt-4">
                    <p className="text-xs text-gray-500 mb-2">Or paste URL:</p>
                    <input
                      type="url"
                      value={formData.featured_image}
                      onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                      className="w-full px-3 py-2 bg-black border border-[#222] text-white text-sm focus:outline-none focus:border-yellow-500"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Category */}
                <div className="bg-[#111] border border-[#222] p-6">
                  <label className="text-xs font-bold uppercase tracking-wider text-yellow-500 block mb-4">
                    Category
                  </label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="w-full bg-black border-[#222] text-white" data-testid="article-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-[#222]">
                      {categoryOptions.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-600 mt-2">
                    Select which section this story appears in
                  </p>
                </div>

                {/* Author */}
                <div className="bg-[#111] border border-[#222] p-6">
                  <label className="text-xs font-bold uppercase tracking-wider text-yellow-500 block mb-4">
                    Author
                  </label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-[#222] text-white focus:outline-none focus:border-yellow-500"
                    placeholder="Author"
                    data-testid="article-author-input"
                  />
                </div>

                {/* Ad Codes */}
                <div className="bg-[#111] border border-[#222] p-6">
                  <label className="text-xs font-bold uppercase tracking-wider text-yellow-500 block mb-4">
                    Ad Codes (Optional)
                  </label>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Top</label>
                      <textarea
                        value={formData.ad_code_top}
                        onChange={(e) => setFormData({ ...formData, ad_code_top: e.target.value })}
                        className="w-full px-2 py-2 bg-black border border-[#222] text-white font-mono text-xs resize-none focus:outline-none focus:border-yellow-500"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Middle</label>
                      <textarea
                        value={formData.ad_code_middle}
                        onChange={(e) => setFormData({ ...formData, ad_code_middle: e.target.value })}
                        className="w-full px-2 py-2 bg-black border border-[#222] text-white font-mono text-xs resize-none focus:outline-none focus:border-yellow-500"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Bottom</label>
                      <textarea
                        value={formData.ad_code_bottom}
                        onChange={(e) => setFormData({ ...formData, ad_code_bottom: e.target.value })}
                        className="w-full px-2 py-2 bg-black border border-[#222] text-white font-mono text-xs resize-none focus:outline-none focus:border-yellow-500"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}

        {/* Share Modal after publishing */}
        {showShareModal && publishedSlug && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" data-testid="share-modal">
            <div className="bg-[#111] border border-[#222] p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold text-lg">Story Published!</h3>
                <button onClick={() => setShowShareModal(false)} className="text-gray-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>

              <p className="text-gray-400 text-sm mb-4">Share it now:</p>

              {/* Send Push Notification */}
              <button
                onClick={async () => {
                  try {
                    const res = await api.post('/admin/push/send', {
                      title: formData.title,
                      body: 'New story just dropped! Tap to read.',
                      url: `/article/${publishedSlug}`,
                    });
                    toast.success(`Push sent to ${res.data.sent} subscribers!`);
                  } catch (e) {
                    toast.error('Failed to send push');
                  }
                }}
                className="w-full flex items-center justify-center gap-2 py-3 bg-yellow-500 text-white font-bold hover:bg-yellow-600 transition-colors mb-2"
                data-testid="send-push-btn"
              >
                <Bell size={16} />
                Send Push Alert
              </button>

              {/* Share Link */}
              <div className="bg-black border border-[#222] p-3 mb-3 flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={`${window.location.origin}/api/og/article/${publishedSlug}`}
                  className="flex-1 bg-transparent text-white text-xs outline-none"
                  data-testid="share-link-input"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/api/og/article/${publishedSlug}`);
                    toast.success('Link copied!');
                  }}
                  className="shrink-0 px-3 py-1.5 bg-yellow-500 text-white text-xs font-bold hover:bg-yellow-600 transition-colors"
                  data-testid="copy-share-link-btn"
                >
                  Copy Link
                </button>
              </div>

              {/* Share to Facebook */}
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${window.location.origin}/api/og/article/${publishedSlug}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#1877F2] text-white font-bold hover:bg-[#166FE5] transition-colors mb-2"
                data-testid="share-facebook-btn"
              >
                Share on Facebook
              </a>

              {/* Share to Twitter */}
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(`${window.location.origin}/api/og/article/${publishedSlug}`)}&text=${encodeURIComponent(formData.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-3 bg-black border border-[#333] text-white font-bold hover:border-white transition-colors"
                data-testid="share-twitter-btn"
              >
                Share on X / Twitter
              </a>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminArticleForm;
